import type { InDesignMasterTemplate } from "hoxton-types";
import type { S3FileLocation } from "hoxton-types/indesign";

import type { InDesignComputedOverwrite } from "@/types/types";
import {
    type PreviewSharedOverride,
    type PreviewLayerManagement,
    type RenditionRequestBody
} from "@/utils/CreativeIntelligenceApi/indesign";
import { EditableType } from "hoxton-shared/enums/editables";

const OUTPUT_FORMAT = "png" as const;
const RESOURCE_TYPE = "ADOBE_INDESIGN_PREVIEW" as const;

function isLikelyRemoteUrl(value?: string): value is string {
    return typeof value === "string" && /^https?:\/\//i.test(value);
}

function isWorkspaceAssetPath(value?: string): value is string {
    return typeof value === "string" && /^(Links|Overrides|Document Fonts)\//.test(value);
}

function getPreviewAssetBucketAndKey(value?: string): Pick<PreviewSharedOverride, "bucket" | "key"> | undefined {
    if (!isLikelyRemoteUrl(value)) {
        return;
    }

    const remoteValue = value;
    const parsedUrl = new URL(remoteValue);
    const hostParts = parsedUrl.hostname.split(".");
    const normalizedPath = decodeURIComponent(parsedUrl.pathname.replace(/^\/+/, ""));

    if (!normalizedPath) {
        return;
    }

    if (hostParts.length >= 4 && hostParts[1] === "s3") {
        return {
            bucket: hostParts[0],
            key: normalizedPath
        };
    }

    if (hostParts.length >= 3 && hostParts[0] === "s3") {
        const [bucket, ...keyParts] = normalizedPath.split("/");
        const key = keyParts.join("/");

        if (!bucket || !key) {
            return;
        }

        return {
            bucket,
            key
        };
    }
}

function resolveImageOverrideValue(
    nextOverride: PreviewSharedOverride,
    override: PreviewSharedOverride,
    matchingDefaultOverride?: PreviewSharedOverride
) {
    if (nextOverride.editableType !== EditableType.Image) {
        return;
    }

    if (nextOverride.bucket || nextOverride.key) {
        nextOverride.value = undefined;
        return;
    }

    const hasExplicitValue = isLikelyRemoteUrl(override.value) || isWorkspaceAssetPath(override.value);

    if (!matchingDefaultOverride || hasExplicitValue) {
        return;
    }

    nextOverride.value = matchingDefaultOverride.value || matchingDefaultOverride.assetDestination || override.value;

    if (matchingDefaultOverride.assetDestination) {
        nextOverride.assetDestination = matchingDefaultOverride.assetDestination;
    }
}

function toFiniteNumber(value: number | string | undefined) {
    const parsedValue =
        typeof value === "number" ? value : typeof value === "string" && value.length ? Number(value) : NaN;

    return Number.isFinite(parsedValue) ? parsedValue : undefined;
}

function toPreviewFontColorHex(value: unknown): string | undefined {
    if (typeof value === "string" && value.length > 0) {
        return value;
    }

    if (!value || typeof value !== "object") {
        return undefined;
    }

    const fontColor = value as { hex?: unknown; swatchName?: unknown };
    return typeof fontColor.hex === "string" ? fontColor.hex : undefined;
}

function normalizeTextOverrideValue(value?: string) {
    if (typeof value !== "string") {
        return value;
    }

    return value
        .replace(/\r\n?/g, "\n")
        .replace(/\s*<br\s*\/?>\s*/gi, "\n")
        .replace(/[ \t]+\n/g, "\n")
        .replace(/\n[ \t]+/g, "\n");
}

function areEquivalentTextOverrideValues(left?: string, right?: string) {
    return (
        typeof left === "string" &&
        typeof right === "string" &&
        normalizeTextOverrideValue(left) === normalizeTextOverrideValue(right)
    );
}

function toPreviewLayerManagement(value: unknown): PreviewLayerManagement | undefined {
    if (!value || typeof value !== "object") {
        return undefined;
    }

    const layerManagement = value as {
        font?: unknown;
        fontSize?: number | string;
        fontStyle?: unknown;
        letterSpacing?: number | string;
        lineHeight?: number | string;
        fontColor?: unknown;
        visible?: unknown;
    };

    const previewFontColor = toPreviewFontColorHex(layerManagement.fontColor);

    const previewLayerManagement = {
        ...(typeof layerManagement.font === "string" ? { font: layerManagement.font } : {}),
        ...(toFiniteNumber(layerManagement.fontSize) !== undefined
            ? { fontSize: toFiniteNumber(layerManagement.fontSize) }
            : {}),
        ...(typeof layerManagement.fontStyle === "string" ? { fontStyle: layerManagement.fontStyle } : {}),
        ...(toFiniteNumber(layerManagement.letterSpacing) !== undefined
            ? { letterSpacing: toFiniteNumber(layerManagement.letterSpacing) }
            : {}),
        ...(toFiniteNumber(layerManagement.lineHeight) !== undefined
            ? { lineHeight: toFiniteNumber(layerManagement.lineHeight) }
            : {}),
        ...(previewFontColor ? { fontColor: previewFontColor } : {}),
        ...(typeof layerManagement.visible === "boolean" ? { visible: layerManagement.visible } : {})
    };

    return Object.keys(previewLayerManagement).length ? previewLayerManagement : undefined;
}

function normalizeSharedOverrides(overrides: PreviewSharedOverride[]) {
    const overridesByKey = new Map<string, PreviewSharedOverride>();

    overrides.forEach(override => {
        overridesByKey.set(override.itemName, override);
    });

    return Array.from(overridesByKey.entries())
        .sort(([leftKey], [rightKey]) => leftKey.localeCompare(rightKey))
        .map(([, override]) => override);
}

function mergeSharedOverride(
    override: PreviewSharedOverride,
    matchingDefaultOverride: PreviewSharedOverride | undefined
): PreviewSharedOverride {
    const nextOverride: PreviewSharedOverride = {
        ...matchingDefaultOverride,
        ...override
    };

    if (matchingDefaultOverride?.layerManagement || override.layerManagement) {
        nextOverride.layerManagement = {
            ...(matchingDefaultOverride?.layerManagement || {}),
            ...(override.layerManagement || {})
        };
    }

    if (
        nextOverride.editableType === EditableType.Text &&
        areEquivalentTextOverrideValues(override.value, matchingDefaultOverride?.value)
    ) {
        nextOverride.value = matchingDefaultOverride?.value;
    }

    resolveImageOverrideValue(nextOverride, override, matchingDefaultOverride);

    return nextOverride;
}

function normalizeTargetSize(targetSize?: { width?: number; height?: number; unit?: string }) {
    const width = toFiniteNumber(targetSize?.width);
    const height = toFiniteNumber(targetSize?.height);

    if (width === undefined || height === undefined) {
        return undefined;
    }

    return {
        width,
        height,
        ...(targetSize?.unit ? { unit: targetSize.unit } : {})
    };
}

function getDefaultSharedOverrides(
    defaultSharedOverrides: InDesignMasterTemplate["defaultSharedOverrides"] | undefined
): PreviewSharedOverride[] {
    return (defaultSharedOverrides || [])
        .filter(override => typeof override?.itemName === "string" && override.itemName.length > 0)
        .map(override => ({
            itemName: override.itemName,
            ...(override.editableType ? { editableType: override.editableType } : {}),
            ...(typeof override.value === "string" ? { value: override.value } : {}),
            ...(typeof override.assetDestination === "string" ? { assetDestination: override.assetDestination } : {}),
            ...(toPreviewLayerManagement(override.layerManagement)
                ? { layerManagement: toPreviewLayerManagement(override.layerManagement) }
                : {})
        }));
}

function getComputedOverrides(computedOverwrites: InDesignComputedOverwrite[] | undefined): PreviewSharedOverride[] {
    return (computedOverwrites || [])
        .filter(overwrite => typeof overwrite.name === "string" && overwrite.name.length > 0)
        .map(overwrite => {
            const imageAssetBucketAndKey =
                overwrite.type === EditableType.Image
                    ? getPreviewAssetBucketAndKey(overwrite.value || undefined)
                    : undefined;

            const layerManagement = toPreviewLayerManagement({
                fontSize: overwrite.settings?.fontSize
            });

            const visibilityLayerManagement =
                overwrite.settings?.visible !== undefined ? { visible: overwrite.settings.visible } : {};
            const mergedLayerManagement =
                layerManagement || visibilityLayerManagement.visible !== undefined
                    ? { ...(layerManagement || {}), ...visibilityLayerManagement }
                    : undefined;

            return {
                itemName: overwrite.name,
                ...(overwrite.type === EditableType.Image
                    ? { editableType: EditableType.Image }
                    : { editableType: EditableType.Text }),
                ...(imageAssetBucketAndKey || {}),
                ...(!imageAssetBucketAndKey ? { value: overwrite.value } : {}),
                ...(mergedLayerManagement && Object.keys(mergedLayerManagement).length
                    ? { layerManagement: mergedLayerManagement }
                    : {})
            };
        });
}

function getOverrides(
    computedOverwrites: InDesignComputedOverwrite[] | undefined,
    masterTemplate: InDesignMasterTemplate
) {
    const computedSharedOverrides = getComputedOverrides(computedOverwrites);
    const defaultSharedOverrides = getDefaultSharedOverrides(masterTemplate.defaultSharedOverrides);

    if (!computedSharedOverrides.length) {
        return normalizeSharedOverrides(defaultSharedOverrides);
    }

    const mergedOverrides = new Map<string, PreviewSharedOverride>();

    defaultSharedOverrides.forEach(override => {
        mergedOverrides.set(override.itemName, override);
    });

    computedSharedOverrides.forEach(override => {
        const matchingDefaultOverride = mergedOverrides.get(override.itemName);
        const resolvedOverrideKey = matchingDefaultOverride?.itemName ?? override.itemName;

        mergedOverrides.set(resolvedOverrideKey, mergeSharedOverride(override, matchingDefaultOverride));
    });

    return normalizeSharedOverrides(Array.from(mergedOverrides.values()));
}

export function getRenditionPreviewRequestBody({
    clientId,
    computedOverwrites,
    masterTemplate,
    sourceDocuments,
    targetSize
}: {
    clientId: string;
    computedOverwrites?: InDesignComputedOverwrite[];
    masterTemplate: InDesignMasterTemplate;
    sourceDocuments: S3FileLocation[];
    targetSize?: { width?: number; height?: number; unit?: string };
}): RenditionRequestBody {
    const sharedOverrides = getOverrides(computedOverwrites, masterTemplate);
    const normalizedTargetSize = normalizeTargetSize(targetSize);

    const renditionRequestBody: RenditionRequestBody = {
        type: RESOURCE_TYPE,
        input: {
            clientId,
            sourceDocuments: sourceDocuments.map(sourceDocument => ({
                bucket: sourceDocument.bucket,
                key: sourceDocument.key
            })),
            outputFormat: OUTPUT_FORMAT,
            ...(normalizedTargetSize ? { targetSize: normalizedTargetSize } : {}),
            sharedOverrides
        }
    };

    return renditionRequestBody;
}
